import { useState, useEffect } from 'react'
import {
  Box, Card, CardContent, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Snackbar, Alert, CircularProgress,
  FormControlLabel, Checkbox,
} from '@mui/material'
import { Save } from '@mui/icons-material'
import { api } from '../../services/api'
import { usePlantContext } from '../../hooks/usePlantContext'

// FAR-76: line capacity, for the 4–18 Month tab's utilization row.
//
// Per line: hours per working day, and changeover hours per run (FAR-94).
// Available hours H for a month = working days × hours/day, with working days
// from the plant calendar the engine plans with. Changeover is load, not a
// deduction: each SKU run on the line that month adds its changeover hours.
// The old monthly changeover field is no longer shown or used.
//
// This replaces a demo screen whose Save button saved nothing. Until real
// numbers are saved, the API returns a labelled placeholder (the old demo
// values, with the deployment's changeover per run) and the banner says so.
// An Admin can tune those demo numbers and save them still marked as
// placeholders, so the board keeps its "Placeholder capacity" tag until
// Faribault's real numbers are in (FAR-94).

const TYPICAL_DAYS = 22 // for the example only; real months use the plant calendar

// Configs saved before FAR-94 have no per-run changeover; show 0, not blank.
// The retired monthly field is dropped so it isn't sent back.
const normalize = (c) => c && {
  ...c,
  placeholder: !!c.placeholder,
  lines: (c.lines || []).map((l) => ({
    line: l.line, hoursPerDay: l.hoursPerDay, changeoverHoursPerRun: l.changeoverHoursPerRun ?? 0,
  })),
}

export default function CapacityConfig() {
  const { plant } = usePlantContext()
  const [config, setConfig] = useState(null)
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    if (!plant?.plantCode) return
    let cancelled = false
    api.get(`/capacity/lines?plant=${encodeURIComponent(plant.plantCode)}`)
      .then((d) => { if (!cancelled) setConfig(normalize(d?.config || d)) })
      .catch((err) => { if (!cancelled) setError(err.message || 'Could not load line capacity') })
    return () => { cancelled = true }
  }, [plant?.plantCode])

  const update = (i, field, value) => {
    setConfig((c) => ({
      ...c,
      lines: c.lines.map((l, j) => (j === i ? { ...l, [field]: value === '' ? '' : Number(value) } : l)),
    }))
  }

  const valid = config?.lines?.every((l) =>
    l.hoursPerDay !== '' && l.hoursPerDay >= 0 && l.hoursPerDay <= 24
    && l.changeoverHoursPerRun !== '' && l.changeoverHoursPerRun >= 0 && l.changeoverHoursPerRun <= 24)

  const save = async () => {
    setSaving(true)
    setError(null)
    try {
      const out = await api.put('/capacity/lines', {
        plant: plant.plantCode, placeholder: config.placeholder, lines: config.lines,
      })
      setConfig(normalize(out))
      setSaved(true)
    } catch (err) {
      setError(err.message || 'Could not save line capacity')
    } finally {
      setSaving(false)
    }
  }

  if (!config && !error) return <CircularProgress size={24} />

  return (
    <Box>
      {config?.placeholder && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          These are <strong>placeholder</strong> hours, not Faribault's, so line utilization on the
          Planning Board is an estimate. Replace them with each line's real hours, untick
          &quot;Placeholder values&quot; and save.
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {config && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h5" sx={{ mb: 0.5 }}>Line capacity</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Available hours = working days × hours per day. Working days come from the plant
              calendar. Each SKU run on a line in a month adds that line&apos;s changeover hours to its load.
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Line</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Hours per working day</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Changeover h / run</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Available hours in a {TYPICAL_DAYS}-day month</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {config.lines.map((l, i) => (
                    <TableRow key={l.line}>
                      <TableCell sx={{ fontWeight: 600 }}>{l.line}</TableCell>
                      <TableCell align="center">
                        <TextField size="small" type="number" value={l.hoursPerDay}
                          onChange={(e) => update(i, 'hoursPerDay', e.target.value)}
                          inputProps={{ min: 0, max: 24, step: 0.5, 'aria-label': `${l.line} hours per day` }}
                          sx={{ width: 100 }} />
                      </TableCell>
                      <TableCell align="center">
                        <TextField size="small" type="number" value={l.changeoverHoursPerRun}
                          onChange={(e) => update(i, 'changeoverHoursPerRun', e.target.value)}
                          inputProps={{ min: 0, max: 24, step: 0.5, 'aria-label': `${l.line} changeover hours per run` }}
                          sx={{ width: 100 }} />
                      </TableCell>
                      <TableCell align="center">
                        {(TYPICAL_DAYS * (Number(l.hoursPerDay) || 0)).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <FormControlLabel sx={{ mt: 1 }}
              control={(
                <Checkbox checked={config.placeholder}
                  onChange={(e) => setConfig((c) => ({ ...c, placeholder: e.target.checked }))}
                  inputProps={{ 'aria-label': 'Placeholder values' }} />
              )}
              label="Placeholder values (not Faribault's real numbers). The board keeps its Placeholder capacity tag." />
            {config.updatedBy && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Last saved by {config.updatedBy}
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={saving ? <CircularProgress size={16} /> : <Save />}
          onClick={save} disabled={!valid || saving}>
          Save line capacity
        </Button>
      </Box>

      <Snackbar open={saved} autoHideDuration={3000} onClose={() => setSaved(false)}>
        <Alert severity="success" onClose={() => setSaved(false)}>Line capacity saved.</Alert>
      </Snackbar>
    </Box>
  )
}
